import React, { useEffect, useState, useCallback } from 'react';
import { revenueAPI, labelAPI } from '../../lib/api';
import { formatCurrency, formatCurrencyFull, formatNumber, formatMonth } from '../../lib/utils';
import { compactInr } from '../../lib/chartFormat';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import TrackAnalyticsModal from '../../components/TrackAnalyticsModal';
import ExcelUploadModal from '../../components/ExcelUploadModal';
import { 
    Upload, 
    TrendingUp, 
    IndianRupee,
    DollarSign,
    Globe,
    Music2,
    FileSpreadsheet,
    Loader2,
    CheckCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useTheme } from '../../context/ThemeContext';

const COLORS = ['#7C3AED', '#00FF9D', '#CCFF00', '#00F2EA', '#FFB700', '#FF4444'];

const RevenuePage = () => {
    const [summary, setSummary] = useState(null);
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLabel, setSelectedLabel] = useState('all');
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadResult, setUploadResult] = useState(null);
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [trackModalOpen, setTrackModalOpen] = useState(false);
    const { isDark } = useTheme();
    
    // Theme-aware chart colors
    const chartColors = {
        grid: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        axis: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)',
        tooltipBg: isDark ? '#0f172a' : '#ffffff',
        tooltipBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        text: isDark ? '#ffffff' : '#1e293b'
    };

    const fetchData = useCallback(async () => {
        try {
            const [summaryRes, labelsRes] = await Promise.all([
                revenueAPI.getSummary(selectedLabel !== 'all' ? selectedLabel : undefined),
                labelAPI.getOptions()
            ]);
            setSummary(summaryRes.data);
            setLabels(labelsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load revenue data');
        } finally {
            setLoading(false);
        }
    }, [selectedLabel]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setUploadProgress(0);
        setUploadResult(null);

        try {
            const response = await revenueAPI.upload(file, (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
            });
            setUploadResult(response.data);
            toast.success(`${response.data.records_processed} revenue records uploaded`);
            fetchData();
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error(error.response?.data?.detail || 'Upload failed');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const monthlyData = summary?.by_month?.slice(0, 6).reverse() || [];
    const platformData = summary?.by_platform?.slice(0, 6) || [];

    return (
        <div className="space-y-6" data-testid="revenue-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold tracking-tight">Revenue Management</h1>
                    <p className="text-muted-foreground">Upload and analyze revenue reports</p>
                </div>
                <Button
                    className="bg-primary hover:bg-primary/90 glow-primary"
                    data-testid="upload-revenue-btn"
                    onClick={() => setUploadDialogOpen(true)}
                >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Revenue Report
                </Button>
                <ExcelUploadModal
                    kind="revenue"
                    validateUrl="/revenue/validate"
                    uploadUrl="/revenue/upload"
                    pollUrlBase="/revenue/import-jobs"
                    open={uploadDialogOpen}
                    onOpenChange={setUploadDialogOpen}
                    onUploaded={(data) => {
                        setUploadResult(data);
                        toast.success(`${(data.processed_rows ?? data.records_processed ?? 0).toLocaleString()} revenue rows imported`);
                        fetchData();
                    }}
                />
            </div>

            {/* Filter */}
            <Card className="bg-card/50 border-border/40">
                <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground">Filter by:</span>
                        <Select value={selectedLabel} onValueChange={setSelectedLabel}>
                            <SelectTrigger className="w-48 bg-background/50" data-testid="revenue-label-filter">
                                <SelectValue placeholder="Select label" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Labels</SelectItem>
                                {labels.map((label) => (
                                    <SelectItem key={label.id} value={label.id}>
                                        {label.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center">
                                <IndianRupee className="w-6 h-6 text-secondary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Gross Revenue</p>
                                <p className="text-2xl font-heading font-bold text-secondary" data-testid="revenue-gross-inr">
                                    {formatCurrency(summary?.totals?.gross_revenue_inr, 'INR')}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">{formatCurrency(summary?.totals?.gross_revenue_usd)} USD</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Label Share</p>
                                <p className="text-2xl font-heading font-bold" data-testid="revenue-warner-share">
                                    {formatCurrency(summary?.totals?.label_payout_inr, 'INR')}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">Paid to labels</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                                <IndianRupee className="w-6 h-6 text-accent" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">EYP Share</p>
                                <p className="text-2xl font-heading font-bold" data-testid="revenue-eyp-share">
                                    {formatCurrency(summary?.totals?.net_revenue_inr, 'INR')}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">Platform net</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-card/50 border-border/40">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Streams</p>
                                <p className="text-2xl font-heading font-bold" data-testid="revenue-streams">
                                    {formatNumber(summary?.totals?.total_units)}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">{formatNumber(summary?.totals?.total_entries)} entries</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Revenue Chart */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading">Monthly Net Revenue</CardTitle>
                        <CardDescription>EYP share over the last 6 months</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={monthlyData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis 
                                        dataKey="month" 
                                        tickFormatter={(val) => val?.slice(5) || ''} 
                                        stroke={chartColors.axis}
                                        tick={{ fill: chartColors.axis, fontSize: 12 }}
                                    />
                                    <YAxis 
                                        stroke={chartColors.axis} 
                                        tick={{ fill: chartColors.axis, fontSize: 12 }}
                                        tickFormatter={compactInr}
                                        width={70}
                                    />
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: chartColors.tooltipBg, 
                                            border: `1px solid ${chartColors.tooltipBorder}`,
                                            borderRadius: '8px',
                                            color: chartColors.text
                                        }}
                                        itemStyle={{ color: chartColors.text }}
                                        labelStyle={{ color: chartColors.text }}
                                        formatter={(value) => [formatCurrencyFull(value, 'INR'), 'Net Revenue']}
                                    />
                                    <Bar dataKey="net_inr" name="Net Revenue" fill="#00FF9D" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Platform Distribution */}
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading">Revenue by Platform</CardTitle>
                        <CardDescription>Distribution across streaming platforms</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={platformData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={5}
                                        dataKey="gross_inr"
                                        nameKey="platform"
                                    >
                                        {platformData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: chartColors.tooltipBg, 
                                            border: `1px solid ${chartColors.tooltipBorder}`,
                                            borderRadius: '8px',
                                            color: chartColors.text
                                        }}
                                        formatter={(value) => formatCurrencyFull(value, 'INR')}
                                    />
                                    <Legend wrapperStyle={{ color: chartColors.text }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-4 justify-center">
                            {platformData.map((platform, index) => (
                                <div key={platform.platform} className="flex items-center gap-2 text-sm">
                                    <div 
                                        className="w-3 h-3 rounded-full" 
                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    />
                                    <span className="text-muted-foreground">{platform.platform}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Top Tracks */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                        <Music2 className="w-5 h-5 text-secondary" />
                        Top Performing Tracks
                    </CardTitle>
                    <CardDescription>Highest earning songs from revenue data</CardDescription>
                </CardHeader>
                <CardContent>
                    {summary?.top_tracks?.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            No revenue data available
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {summary?.top_tracks?.map((track, index) => (
                                <div 
                                    key={index} 
                                    className="flex items-center gap-4 p-3 rounded-xl bg-muted/20 hover:bg-muted/30 transition-colors group cursor-pointer"
                                    onClick={() => {
                                        setSelectedTrack(track.title);
                                        setTrackModalOpen(true);
                                    }}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold shrink-0">
                                        {index + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate group-hover:text-primary transition-colors">
                                            {track.title}
                                        </p>
                                        <p className="text-sm text-muted-foreground truncate">
                                            {track.artist} • {formatNumber(track.units)} streams
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="font-mono font-bold text-secondary">
                                            {formatCurrency(track.gross_inr || track.gross_usd, 'INR')}
                                        </p>
                                        <p className="text-xs text-muted-foreground">revenue</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
            
            {/* Track Analytics Modal */}
            <TrackAnalyticsModal 
                open={trackModalOpen}
                onOpenChange={setTrackModalOpen}
                trackName={selectedTrack}
            />
        </div>
    );
};

export default RevenuePage;
